import React, { useRef, useEffect, useState } from 'react';
import './index.less';

interface PhotoCaptureProps {
    onClose: () => void;
    onCapture: (imageData: string, hash: string) => void;
    hash: string;
}

const PhotoCapture: React.FC<PhotoCaptureProps> = ({ onClose, onCapture, hash }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [isCapturing, setIsCapturing] = useState(false);

    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    useEffect(() => {
        // Request camera access with rear camera preference
        navigator.mediaDevices.getUserMedia({ 
            video: { 
                facingMode: { exact: "environment" }
            } 
        })
            .then((mediaStream) => {
                setStream(mediaStream);
                if (videoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                }
            })
            .catch((error) => {
                console.error('Error accessing camera:', error);
                // 如果后置摄像头不可用，尝试使用前置摄像头
                navigator.mediaDevices.getUserMedia({ 
                    video: { 
                        facingMode: "user"
                    } 
                })
                .then((mediaStream) => {
                    setStream(mediaStream);
                    if (videoRef.current) {
                        videoRef.current.srcObject = mediaStream;
                    }
                })
                .catch((fallbackError) => {
                    console.error('Error accessing front camera:', fallbackError);
                });
            });

        // Cleanup function
        return () => {
            stopCamera();
        };
    }, []);

    const handleClose = () => {
        stopCamera();
        onClose();
    };

    const handleCapture = () => {
        if (!videoRef.current || !canvasRef.current) return;

        setIsCapturing(true);
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        if (!context) return;

        // Set canvas dimensions to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw the current video frame on the canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert canvas to base64 image data
        const imageData = canvas.toDataURL('image/jpeg');
        
        // 立即停止摄像头
        stopCamera();
        
        // 调用父组件的onCapture回调
        onCapture(imageData, hash);
        setIsCapturing(false);

        // 拍照完成统计
        if (typeof gtag !== 'undefined') {
            gtag('event', 'photo_capture_complete', {
                event_category: 'photo',
                event_label: hash || 'photo_capture',
                value: 1
            });
        }

    };

    return (
        <div className="photo-capture-overlay">
            <div className="photo-capture-content">
                <div className="camera-container">
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        className="camera-preview"
                    />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                    <div className="camera-outline" />
                    <div className="tip">请对准OC卡，一张卡只能拍摄一次噢</div>
                </div>
                <div className="controls">
                    <button 
                        className="capture-button"
                        onClick={handleCapture}
                        disabled={isCapturing}
                    >
                        {isCapturing ? '拍摄中...' : '拍摄'}
                    </button>
                    <button 
                        className="close-button"
                        onClick={handleClose}
                    >
                        关闭
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PhotoCapture; 